import { cn } from '@talentmesh/ui/lib/utils';

import { type MRT_Header, type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type BoxProps } from '../../types/mrt-ui-props';
import { MRT_Box } from '../mrt/MRT_Box';
import classes from './MRT_TableHeadCellResizeHandle.module.css';

interface Props<TData extends MRT_RowData> extends BoxProps {
    header: MRT_Header<TData>;
    table: MRT_TableInstance<TData>;
}

export const MRT_TableHeadCellResizeHandle = <TData extends MRT_RowData>({ header, table, ...rest }: Props<TData>) => {
    const {
        getState,
        options: { columnResizeDirection, columnResizeMode },
        setColumnSizingInfo,
    } = table;
    const { density } = getState();
    const { column } = header;
    const handler = header.getResizeHandler();

    const offset =
        column.getIsResizing() && columnResizeMode === 'onEnd'
            ? `translateX(${
                  (columnResizeDirection === 'rtl' ? -1 : 1) * (getState().columnSizingInfo.deltaOffset ?? 0)
              }px)`
            : undefined;

    const {
        __vars: restVars,
        className: restClassName,
        ...restBox
    } = rest as {
        __vars?: Record<string, number | string | undefined>;
    } & BoxProps;

    return (
        <button className={classes['root-button']} type="button">
            <MRT_Box
                onDoubleClick={() => {
                    setColumnSizingInfo((old) => ({
                        ...old,
                        isResizingColumn: false,
                    }));
                    column.resetSize();
                }}
                onMouseDown={handler}
                onTouchStart={handler}
                role="separator"
                {...restBox}
                __vars={{ '--mrt-transform': offset, ...restVars }}
                className={cn(
                    'mrt-table-head-cell-resize-handle',
                    classes.root,
                    classes[`root-${columnResizeDirection}`],
                    !header.subHeaders.length && columnResizeMode === 'onChange' && classes['root-hide'],
                    density,
                    restClassName,
                )}
            />
        </button>
    );
};
